import { navigate } from 'gatsby';
import { reaction } from 'mobx';
import { useEffect } from 'react';

import {
  getNativePlatform,
  isInNativeApp,
  nativeBridge,
  syncTokenFromNative,
} from '../../bridge';
import type {
  AuthTokenRefreshedPayload,
  PushRegisterResponsePayload,
} from '../../bridge/types';
import { CLINIC_STORAGE_KEYS } from '../../constants/buyer-portal';
import { ENDPOINTS } from '../../services/endpoints';
import { clinicHttp } from '../../services/http';
import { useBuyerStores } from '../../stores/buyer/buyer-store-context';

const APP_RESUMED_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;
const PUSH_REGISTER_TIMEOUT_MS = 15000;

/**
 * Wires the Native Bridge into the running Web app. Only does anything when
 * `isInNativeApp()` — in a plain browser it's a no-op so the existing web
 * flows remain untouched.
 *
 * Mounted once at the root, inside BuyerStoreProvider, so it can refresh
 * stores on APP_RESUMED.
 */
export const NativeBridgeBootstrap = (): null => {
  const { authStore, orderStore, cartStore } = useBuyerStores();

  useEffect(() => {
    if (!isInNativeApp()) return;

    // Primary sync runs in gatsby-browser.js BEFORE the store provider mounts.
    // This second call is a race-condition guard: if `__VMALL_NATIVE__` was
    // injected after module-eval, the early sync would have been a no-op and
    // authStore hydrated empty — re-sync now and rehydrate the store so the
    // UI flips from guest to authed without a reload.
    if (syncTokenFromNative()) {
      authStore.hydrate();
    }
    // Native pushes AUTH_TOKEN_REFRESHED on (a) proactive refresh before web
    // hits 401, AND (b) post-login token delivery when native is the one that
    // handled sign-in. Write the new token to localStorage, re-sync from
    // `__VMALL_NATIVE__.auth` in case `auth.user` was refreshed alongside,
    // then rehydrate the store so MobX observers flip off the guest snapshot
    // without requiring a manual page reload.
    const unsubTokenRefresh = nativeBridge.on('AUTH_TOKEN_REFRESHED', (payload) => {
      const data = payload as AuthTokenRefreshedPayload | undefined;
      if (!data?.accessToken) return;
      try {
        localStorage.setItem(CLINIC_STORAGE_KEYS.TOKEN, data.accessToken);
      } catch (err) {
        console.warn('[NativeBridgeBootstrap] persist refreshed token failed:', err);
      }
      syncTokenFromNative();
      authStore.hydrate();
    });

    const unsubPushOpened = nativeBridge.on('PUSH_OPENED', (payload) => {
      if (!payload) return;
      const target =
        payload.deeplink ??
        (payload.orderId ? `/orders/${payload.orderId}` : null);
      if (target) navigate(target);
    });

    const unsubDeeplink = nativeBridge.on('DEEPLINK_OPENED', (payload) => {
      if (!payload?.path) return;
      navigate(payload.path);
    });

    const unsubAppResumed = nativeBridge.on('APP_RESUMED', (payload) => {
      if (!payload) return;
      if (payload.backgroundDurationMs <= APP_RESUMED_REFRESH_THRESHOLD_MS) return;
      // Only refresh data the user can actually see right now; guest visitors
      // have no order list, and reloading a logged-out cart is harmless.
      if (authStore.currentUser) {
        orderStore.loadList().catch(() => undefined);
      }
      cartStore.load().catch(() => undefined);
    });

    // Tell native: web listeners are registered, safe to flush pending events.
    nativeBridge.notify({ type: 'WEB_READY' });

    // Push-token registration. Fires whenever an authed user lands — startup
    // (already logged in via hydrated token) OR fresh sign-in/replace-tokens.
    // Dedupes per userId so the same session won't re-POST. Native owns the
    // DELETE on logout (authStore.signOut → notifyLogout), so no cleanup here.
    let lastRegisteredUserId: string | null = null;
    const disposePush = reaction(
      () => (authStore.isAuthed ? authStore.currentUser?.id ?? null : null),
      async (userId) => {
        if (!userId || userId === lastRegisteredUserId) return;
        try {
          const result = await nativeBridge.request<PushRegisterResponsePayload>(
            { type: 'PUSH_REGISTER_REQUEST' },
            PUSH_REGISTER_TIMEOUT_MS,
          );
          // PERMISSION_DENIED / NOT_SUPPORTED / UNKNOWN — silent best-effort.
          if (!result?.success || !result.token) return;
          await clinicHttp.post(ENDPOINTS.BUYER.PUSH_TOKENS, {
            token: result.token,
            platform: getNativePlatform(),
            appVersion: window.__VMALL_NATIVE__?.appVersion ?? 'unknown',
          });
          lastRegisteredUserId = userId;
        } catch (err) {
          // Bridge timeout / backend 4xx-5xx — leave lastRegisteredUserId
          // unchanged so the next mount or login transition can retry.
          console.warn('[NativeBridgeBootstrap] push registration failed:', err);
        }
      },
      { fireImmediately: true },
    );

    return () => {
      unsubTokenRefresh();
      unsubPushOpened();
      unsubDeeplink();
      unsubAppResumed();
      disposePush();
    };
  }, [authStore, orderStore, cartStore]);

  return null;
};

export default NativeBridgeBootstrap;
